import { Container } from '@/components';
import { media } from '@/styles/breakpoints';
import { motion } from 'motion/react';
import styled, { keyframes } from 'styled-components';

export const pulse = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50%       { opacity: 0.8; transform: scale(1.08); }
`;

export const HeroSection = styled.section`
  position: relative;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.navy[950]};
`;

export const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

export const HeroGrid = styled.div`
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    circle at 1px 1px,
    rgba(255, 255, 255, 0.07) 1px,
    transparent 0
  );
  background-size: 40px 40px;
`;

export const HeroBlob = styled.div<{ $x: string; $y: string; $size: string; $delay?: string }>`
  position: absolute;
  left: ${({ $x }) => $x};
  top: ${({ $y }) => $y};
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 168, 240, 0.18) 0%, transparent 70%);
  animation: ${pulse} 8s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay ?? '0s'};
  pointer-events: none;
`;

export const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(6, 18, 28, 0) 0%,
    rgba(6, 18, 28, 0.4) 60%,
    rgba(6, 18, 28, 0.95) 100%
  );
`;

export const HeroContent = styled(Container)`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[8]};
  padding-top: ${({ theme }) => theme.spacing[32]};
  padding-bottom: ${({ theme }) => theme.spacing[24]};
`;

export const HeroEyebrow = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

export const EyebrowLine = styled(motion.span)`
  display: block;
  height: 2px;
  background-color: ${({ theme }) => theme.colors.cyan[400]};
  border-radius: ${({ theme }) => theme.radii.full};
`;

export const EyebrowText = styled(motion.span)`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.cyan[400]};
`;

export const HeroHeading = styled(motion.h1)`
  font-size: clamp(2.5rem, 6vw + 1rem, 5.5rem);
  font-weight: ${({ theme }) => theme.fontWeights.extrabold};
  line-height: ${({ theme }) => theme.lineHeights.tight};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tighter};
  color: white;
  max-width: 16ch;
`;

export const HeroSub = styled(motion.p)`
  font-size: clamp(1rem, 1.5vw + 0.5rem, 1.25rem);
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  color: rgba(255, 255, 255, 0.6);
  max-width: 52ch;
`;

export const HeroCtas = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
`;

export const HeroScroll = styled(motion.div)`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing[8]};
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export const ScrollDot = styled(motion.span)`
  display: block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.cyan[400]};
`;

export const ScrollLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
`;

export const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing[12]};
  ${media.laptop} {
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[16]};
  }
`;

export const AboutVisual = styled.div`
  position: relative;
  height: 360px;
  border-radius: ${({ theme }) => theme.radii.xl};
  overflow: hidden;
  ${media.tablet} {
    height: 480px;
  }
`;

export const AboutVisualBg = styled.div`
  width: 100%;
  height: 120%;
  margin-top: -10%;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.navy[700]} 0%,
    ${({ theme }) => theme.colors.steel[500]} 50%,
    ${({ theme }) => theme.colors.cyan[400]} 100%
  );
`;

export const AboutVisualPattern = styled.div`
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    circle at 1px 1px,
    rgba(255, 255, 255, 0.12) 1px,
    transparent 0
  );
  background-size: 24px 24px;
`;

export const AboutVisualCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export const BigNumber = styled.span`
  font-size: clamp(5rem, 12vw, 9rem);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.04em;
  color: rgba(255, 255, 255, 0.15);
  user-select: none;
`;

export const BigLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: ${({ theme }) => theme.letterSpacings.wide};
  text-transform: uppercase;
`;

export const StatDivider = styled.div`
  width: 32px;
  height: 3px;
  border-radius: ${({ theme }) => theme.radii.full};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.steel[500]},
    ${({ theme }) => theme.colors.cyan[400]}
  );
  margin-bottom: ${({ theme }) => theme.spacing[10]};
`;

export const StatsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[10]};
  margin-top: ${({ theme }) => theme.spacing[12]};

  & > div {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing[10]};
    width: 100%;
  }

  /* StaggerItem */
  & > div > div {
    flex: 1 1 120px;
  }
`;

export const EventsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[5]};
  margin-top: ${({ theme }) => theme.spacing[10]};

  /* O StaggerContainer dentro ocupa 100% */
  & > div {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing[5]};
    width: 100%;
  }

  /* Cada StaggerItem = 1 card */
  & > div > div {
    flex: 1 1 100%;
  }

  ${media.tablet} {
    & > div > div {
      flex: 1 1 calc(50% - ${({ theme }) => theme.spacing[5]});
      max-width: calc(50% - ${({ theme }) => theme.spacing[5]} / 2);
    }
  }

  ${media.desktop} {
    & > div > div {
      flex: 1 1 calc(25% - ${({ theme }) => theme.spacing[5]});
      max-width: calc(25% - ${({ theme }) => theme.spacing[5]} * 3 / 4);
    }
  }
`;

export const EventsSeeAll = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing[10]};
`;

export const NewsGrid = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[5]};
  margin-top: ${({ theme }) => theme.spacing[10]};
`;

export const CtaSection = styled.section`
  position: relative;
  padding-block: ${({ theme }) => theme.spacing[24]};
  overflow: hidden;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.navy[900]} 0%,
    ${({ theme }) => theme.colors.navy[700]} 50%,
    ${({ theme }) => theme.colors.steel[500]} 100%
  );
  ${media.desktop} {
    padding-block: ${({ theme }) => theme.spacing[32]};
  }
`;

export const CtaGlow = styled.div`
  position: absolute;
  top: -120px;
  right: -80px;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 168, 240, 0.25) 0%, transparent 70%);
  pointer-events: none;
`;

export const CtaContent = styled(Container)`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing[6]};
`;

export const CtaButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing[2]};
`;
